import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { getBasicSupplier } from '../../../../redux/services/buyersService';
import dillLogoWithText from '../../../../assets/svg/dill_logo_with_text.svg';
import { AppInputField } from '../../../general/AppInputField/AppInputField';
import { AppButton } from '../../../general/AppButton/AppButton';
import {
  getApplicationHistory,
  getCreditApplication,
} from '../../../../redux/services/creditApplicationsService';
import { updateReferenceInCreditApp } from '../../../../redux/services/creditApplicationsReferenceService';
import { openDillToast } from '../../../../utils/helpers';
import JobSavingSuccess from '../../../../pages/RequestJobInformationSheet/JobSavingSuccess';

enum ModalState {
  SEND_REFERENCE,
  SUCCESS_SENDING_REFERENCE,
}
export const CreditApplicationUpdateReferenceEmail = () => {
  const dispatch = useAppDispatch();
  const [referenceId, setReferenceId] = useState('');
  const [referencePos, setReferencePos] = useState('');
  const [userEmail, setUserEmail] = useState('');

  const { creditApplication } = useAppSelector((state) => state.creditApplications);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [sending, setSending] = useState(false);
  const [modalState, setModalState] = useState(ModalState.SEND_REFERENCE);
  const [modalDetails, setModalDetails] = useState<{
    modalType: 'Success' | 'Error';
    title: string;
    details: string;
  }>({
    modalType: 'Success',
    title: '',
    details: '',
  });

  const { basicSupplier } = useAppSelector((state) => state.buyers);

  const fetchDataUsingTemplateId = async (id: string) => {
    const response = await dispatch(getCreditApplication({ creditApplicationId: id }));
    if (response.type === 'creditApplications/getCreditApplication/rejected') {
      await dispatch(getApplicationHistory({ creditApplicationId: id }));
    }
  };

  const fetchDataUsingDomainId = async (domainId: string) => {
    await dispatch(getBasicSupplier({ supplierDomainId: domainId }));
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paramEmail = params.get('email');
    if (paramEmail) {
      setUserEmail(paramEmail);
    }
    const paramCreditApplicationId = params.get('creditApplicationId');
    if (paramCreditApplicationId) {
      fetchDataUsingTemplateId(paramCreditApplicationId);
    }
    const paramDomainId = params.get('domainId');
    if (paramDomainId) {
      fetchDataUsingDomainId(paramDomainId);
    }
  }, []);

  useEffect(() => {
    if (creditApplication) {
      const foundEmails: string[] = creditApplication.fieldValues
        .filter(
          (field) =>
            field.stepName.toLowerCase().trim().includes('trade') &&
            field.isFieldEmail &&
            field.id.includes('email')
        )
        .map((field) => field.value);
      if (!foundEmails.includes(userEmail)) {
        setModalState(ModalState.SUCCESS_SENDING_REFERENCE);
        setModalDetails({
          modalType: 'Error',
          title: 'Error',
          details: 'You have not been invited to see this credit application.',
        });
      }
      const matchingEmailFields = creditApplication.fieldValues.filter(
        (field) =>
          field.stepName.toLowerCase().trim().includes('trade') &&
          field.isFieldEmail &&
          field.id.includes('email') &&
          field.value === userEmail
      );
      // The same email can be listed on more than one reference; `pos` in the link picks the one
      // this request was for. Links sent before `pos` was added fall back to the first match.
      const pos = new URLSearchParams(location.search).get('pos');
      const foundEmail =
        matchingEmailFields.find(
          (field) => `${field.groupedPosition}**${field.groupStepPosition}` === pos
        ) ?? matchingEmailFields[0];
      if (foundEmail) {
        setReferenceId(foundEmail.uniqueId);
        setReferencePos(`${foundEmail.groupedPosition}**${foundEmail.groupStepPosition}`);
      }
    }
  }, [creditApplication, userEmail]);

  const validate = () => {
    let error = false;
    const emailRegex = /[\w.+-]+@[\w-]+\.[a-z]{1,}/gi;
    if (email.trim() === '') {
      setEmailError('Email is required');
      error = true;
    } else if (email.trim() !== '' && !emailRegex.test(email)) {
      setEmailError('Email is invalid');
      error = true;
    } else if (email.trim().toLowerCase() === userEmail.toLowerCase()) {
      setEmailError('Email should be different from the previous one.');
      error = true;
    }

    return error;
  };

  const handleSubmit = async () => {
    if (validate()) return;

    setSending(true);
    const response = await dispatch(
      updateReferenceInCreditApp({
        data: {
          referenceId,
          email,
          creditApplicationId: creditApplication?.id ?? '',
          referencePos,
        },
      })
    );
    if (response.type.includes('fulfilled')) {
      setSending(false);
      setModalState(ModalState.SUCCESS_SENDING_REFERENCE);
      setModalDetails({
        modalType: 'Success',
        title: 'Success',
        details: 'You have successfully updated the trade reference email.',
      });
    } else {
      setSending(false);
      openDillToast({
        type: 'ERROR',
        message: 'Failed to update the trade reference email.',
      });
    }
    setSending(false);
  };

  return (
    <div className="flex w-full h-full justify-center items-center">
      <div className="flex flex-col w-11/12 md:w-4/6 lg:w-3/6 items-center justify-center mx-4">
        {basicSupplier && basicSupplier.logo && (
          <div className="flex flex-col items-center mb-14">
            <img className="w-2/5" src={basicSupplier.logo.url} />
            <div className="flex items-center mt-2">
              <p>Powered By</p>
              <a href="https://usedill.com/" target="_blank">
                <img className="h-8 ml-2" src={dillLogoWithText} />
              </a>
            </div>
          </div>
        )}
        {(!basicSupplier || (basicSupplier && !basicSupplier.logo)) && (
          <div className="flex flex-col items-center mb-14 w-full">
            <img className="w-2/5" src={dillLogoWithText} />
          </div>
        )}
        {modalState === ModalState.SEND_REFERENCE && (
          <div className="flex flex-col GREY_100-BG p-2 md:p-10 rounded-xl w-full mb-3">
            <div className="w-full flex flex-row justify-center text-sm mb-2">
              Enter the email address of the right person for the trade reference.
            </div>
            <div>
              <AppInputField
                label={'Enter email address'}
                isRequired={true}
                value={email}
                onBlur={() => {
                  setEmail(email.toLowerCase().trim());
                }}
                onTextChange={(text) => {
                  setEmail(text);
                  setEmailError('');
                }}
                errorText={emailError}
              />
            </div>
            <div className="w-full flex flex-row justify-center mt-4">
              <AppButton
                text={'Send'}
                isDisabled={email === '' || sending}
                isLoading={sending}
                onClick={handleSubmit}
              />
            </div>
          </div>
        )}
        {modalState === ModalState.SUCCESS_SENDING_REFERENCE && (
          <div className="flex flex-col items-center w-full justify-center">
            <div className="w-[calc(100%-30px)] md:w-[512px]">
              <JobSavingSuccess
                type={modalDetails.modalType}
                details={modalDetails.details}
                title={modalDetails.title}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
